/**
 * Drawer Component JS
 * Toggles drawer items open/closed with accessible state management.
 * Animation handled entirely by CSS (grid-template-rows transition).
 *
 * Deep-linking: if the URL hash points to an element inside a drawer
 * (e.g. #957cmr7), the containing drawer(s) open and the target is
 * scrolled into view — on initial load and on subsequent hash changes.
 */

// Keep in sync with $transition-med (.3s) in the drawer SCSS.
const DRAWER_TRANSITION_MS = 300;

class Drawer {
	constructor() {
		this.headings = document.querySelectorAll( '.drawer__heading' );
		if ( ! this.headings.length ) return;
		this.init();
	}

	init() {
		this.headings.forEach( heading => {
			heading.addEventListener( 'click', () => this.toggle( heading ) );
		} );

		document.querySelectorAll( '.drawer__content-body table' ).forEach( table => {
			const wrapper = document.createElement( 'div' );
			wrapper.className = 'drawer__table-scroll';
			table.parentNode.insertBefore( wrapper, table );
			wrapper.appendChild( table );
		} );

		// Open + scroll to a drawer target referenced by the URL hash.
		this.openFromHash();
		window.addEventListener( 'hashchange', () => this.openFromHash() );
	}

	/**
	 * Set a single drawer item's open/closed state and sync ARIA.
	 * @param {HTMLElement} item  The .drawer__item element.
	 * @param {boolean}     open  Whether it should end up open.
	 */
	setOpen( item, open ) {
		const heading = item.querySelector( ':scope > .drawer__heading' );
		const panel   = heading && document.getElementById( heading.getAttribute( 'aria-controls' ) );

		item.classList.toggle( 'drawer__item--open', open );

		if ( heading ) {
			heading.setAttribute( 'aria-expanded', open ? 'true' : 'false' );
		}
		if ( panel ) {
			panel.setAttribute( 'aria-hidden', open ? 'false' : 'true' );
		}
	}

	toggle( heading ) {
		const item   = heading.closest( '.drawer__item' );
		const isOpen = item.classList.contains( 'drawer__item--open' );
		this.setOpen( item, ! isOpen );
	}

	openFromHash() {
		const id = decodeURIComponent( window.location.hash.replace( /^#/, '' ) );
		if ( ! id ) return;

		const target = document.getElementById( id );
		if ( ! target ) return;

		// Only act when the target is a drawer item or lives inside one —
		// covers links to the item id, its heading, or content within it.
		if ( ! target.closest( '.drawer__item' ) ) return;

		// Open the target's drawer and any drawers it's nested within.
		let wasClosed = false;
		let node      = target.closest( '.drawer__item' );
		while ( node ) {
			if ( ! node.classList.contains( 'drawer__item--open' ) ) {
				this.setOpen( node, true );
				wasClosed = true;
			}
			node = node.parentElement && node.parentElement.closest( '.drawer__item' );
		}

		// If we opened anything, wait for the grid transition before scrolling
		// so the panel has its final height; otherwise scroll right away.
		const scroll = () => target.scrollIntoView( { behavior: 'smooth', block: 'start' } );
		if ( wasClosed ) {
			window.setTimeout( scroll, DRAWER_TRANSITION_MS + 50 );
		} else {
			scroll();
		}
	}
}

new Drawer();
